import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import useCreateFlashcardStudySession from "../queries/use-create-flashcard-study-session"
import useUpdateFlashcardStudySession from "../queries/use-update-flashcard-study-session"
import type { FlashcardActivity } from "@/services/api/odin/activities/flashcards/study-session/update"
import { usePreventNavigation } from "@/modules/activities/hooks/use-prevent-navigation"

import Header from "./header"
import Content from "./content"
import Footer from "./footer"

import FlashcardDeckHeader from "./header/flashcard-deck-header"
import FlashcardDeckContent from "./content/flashcard-deck"
import FlashcardDeckFooter from "./footer/flashcard-deck-footer"

import FlashcardFeedbackHeader from "./header/flashcard-feedback-header"
import FlashcardFeedbackContent from "./content/flashcard-feedback"
import FlashcardFeedbackFooter from "./footer/flashcard-feedback-footer"
import { useLeftActivityAlertStore } from "../../store/use-left-activity-alert"
import useGetWeeklyObjectivesApproval from "@/modules/planner/queries/use-get-weekly-objectives-approval"
import useInvalidateStudyPlannerQueries from "@/modules/activities/hooks/use-invalidate-queries"
import { useProductTourV2 } from "@/modules/product-tour-v2/stores/use-product-tour"
import { useAuth } from "@/modules/auth/store"
import { PRODUCT_TOUR_V2_STEPS } from "@/modules/product-tour-v2/types/steps"

interface FlashcardsProps {
  id: string
  name: string
  course: string
  userActivityId: string
  userWeeklyObjectiveId: string
}

export default function Flashcards({
  id,
  name,
  course,
  userActivityId,
  userWeeklyObjectiveId,
}: FlashcardsProps) {
  const navigator = useNavigate()
  const createSessionMutation = useCreateFlashcardStudySession()
  const updateSessionMutation = useUpdateFlashcardStudySession()
  const [showAnswer, setShowAnswer] = useState(false)
  const { setCurrentStep } = useProductTourV2()
  const { user } = useAuth()
  const [activity, setActivity] = useState<FlashcardActivity>({
    id: "",
    more: true,
    next: {
      index: 0,
      question: "",
      answer: "",
    },
    total: 0,
    current: 0,
    feedback: {
      bullets: [],
      approvalScore: 0,
      scorePercentage: 0,
    },
  })
  const { onOpen } = useLeftActivityAlertStore()
  const { data, refetch, isPending } = useGetWeeklyObjectivesApproval(userWeeklyObjectiveId)
  const handleRefetchQueries = useInvalidateStudyPlannerQueries()

  // Use the hook to prevent navigation via browser controls
  usePreventNavigation()

  useEffect(() => {
    createStudySession()
  }, [])

  useEffect(() => {
    if (!activity.more) {
      refetch()
    }
  }, [activity.more])

  const createStudySession = async () => {
    try {
      const params = { id, body: { userActivityId } }
      const resp = await createSessionMutation.mutateAsync(params)
      setActivity({
        ...activity,
        ...resp.data.description,
      })
    } catch (err) {
      console.error(err)
    }
  }

  const handleOnClickRating = async (confidence: number) => {
    try {
      const resp = await updateSessionMutation.mutateAsync({
        sessionId: id,
        activityId: activity.id,
        confidence,
      })

      setActivity(resp.data.description)
      setShowAnswer(false)
    } catch (err) {
      console.error(err)
    }
  }

  const handleOnClickShowAnswer = () => {
    setShowAnswer((a) => !a)
  }

  const handleOnContinueStudying = () => {
    handleRefetchQueries()
    if (data?.status === "approved") {
      navigator(`/comunications/${userWeeklyObjectiveId}`)
      return
    }
    if (user.productTour === null) {
      setCurrentStep(PRODUCT_TOUR_V2_STEPS.MY_SITUATION)
      navigator("/my-situation")
      return
    }
    navigator("/study")
  }

  const handleOnRetryActivity = () => {
    createStudySession()
  }

  const handleOnClose = () => {
    handleRefetchQueries()
    if (activity.more) {
      onOpen()
      return
    }
    if (user.productTour === null) {
      setCurrentStep(PRODUCT_TOUR_V2_STEPS.MY_SITUATION)
      navigator("/my-situation")
      return
    }
    navigator("/study")
  }

  const handleOnEditFlashcard = (question: string, answer: string) => {
    setActivity({
      ...activity,
      next: {
        ...activity.next,
        question,
        answer,
      },
    })
  }

  if (createSessionMutation.isPending) {
    return (
      <section className="flex size-full flex-col items-center justify-center">Cargando...</section>
    )
  }

  if (activity.more) {
    return (
      <section className="flex size-full flex-col bg-white p-2 lg:p-10">
        <Header>
          <FlashcardDeckHeader
            activity={activity}
            courseName={course}
            activityName={name}
            onClose={handleOnClose}
          />
        </Header>
        <Content>
          <FlashcardDeckContent
            activity={activity}
            showAnswer={showAnswer}
            handleOnEditFlashcard={handleOnEditFlashcard}
          />
        </Content>
        <Footer>
          <FlashcardDeckFooter
            showAnswer={showAnswer}
            isError={updateSessionMutation.isError}
            isSuccess={updateSessionMutation.isSuccess}
            isLoading={updateSessionMutation.isPending}
            onClickRating={handleOnClickRating}
            onClickShowAnswer={handleOnClickShowAnswer}
          />
        </Footer>
      </section>
    )
  }

  return (
    <section className="flex size-full flex-col bg-gradient-to-b from-primary-50 to-white p-2 lg:p-10">
      <Header>
        <FlashcardFeedbackHeader onClose={handleOnClose} />
      </Header>
      <Content>
        <FlashcardFeedbackContent feedback={activity.feedback} />
      </Content>
      <Footer>
        <FlashcardFeedbackFooter
          activity={activity}
          onContinueStudying={handleOnContinueStudying}
          onRetry={handleOnRetryActivity}
          isPending={isPending}
        />
      </Footer>
    </section>
  )
}
